import { Component, h, Prop, State, Watch } from '@stencil/core';
import { translations, markets } from './pn-find-price-translations';
import { ProductPriceSourceData, ProductPriceItem, FindPriceWeight, PnfindPriceMarket, PnfindPricei18n, FindPriceSizeCategory } from './pn-find-price-types';
import { weights, sizeCategories, fileLocation } from './data.js';
import { convertFileForSizeCategogy } from '../../../globals/shared/productprice/convert.js';
import { state } from './pn-find-price-store';
import small from './img/small-package.svg';
import medium from './img/medium-package.svg';
import large from './img/large-package.svg';
import person from './img/postnord-person1.svg';
import clouds from './img/clouds.svg';

@Component({
  tag: 'pn-find-price',
  styleUrl: 'pn-find-price.scss',
})
export class PnfindPrice {
  @Prop() source: string = fileLocation;
  @Prop({ mutable: true }) language: string = 'sv';
  @Prop({ mutable: true }) market: string = 'se';

  @State() filteredItems: ProductPriceItem[] = [];
  @State() weight: FindPriceWeight = { value: 0, name: '' } as FindPriceWeight;
  @State() weightvalue: number = 0;
  @State() sourceData: ProductPriceSourceData = { types: [], data: [] };
  @State() sizecategory: FindPriceSizeCategory = { value: '', name: '', icon: '', unit: '' } as FindPriceSizeCategory;

  componentWillLoad() {
    this.setState();
    this.getDataSource();
  }

  async getDataSource() {
    const cacheBusting = `?cache=${Math.floor(Math.random() * (10000 - 1) + 1)}`;
    const req = await fetch(this.source + cacheBusting);
    const reqbody = await req.blob();
    try {
      const convertedData = await convertFileForSizeCategogy(reqbody);
      this.sourceData = convertedData;

      //for keeping small value preselected
      this.sizecategory = (sizeCategories as FindPriceSizeCategory[]).filter(x => x.value === 'Small')[0];

      this.filterItems();
    } catch (e) {
      console.log('Unable to parse data');
    }
  }

  @Watch('market')
  @Watch('language')
  setState() {
    if (this.market) {
      state.market = markets[this.market] as PnfindPriceMarket;
    }
    if (this.language && translations[this.language]) {
      state.i18n = translations[this.language] as PnfindPricei18n;
    }
  }

  @Watch('sizecategory')
  @Watch('weightvalue')
  filterItems() {
    if (!this.sourceData?.data) {
      console.log('Data has not yet been loaded');
      return;
    }

    //to handle withing country search
    const deliveryScopeData = this.sourceData.data.filter(x => x.deliveryscope.toLocaleLowerCase() === state.market.country.toLocaleLowerCase());
    if (!this.sizecategory) {
      return;
    }

    const sizeItemData = deliveryScopeData.filter(x => x.sizecategory.toLocaleLowerCase() === this.sizecategory.value.toLocaleLowerCase());

    if (!this.weight) {
      return;
    }
    const comparisonWeight = this.weight.value <= 20 ? this.weight.value * 1000 : this.weight.value;

    let weightFilteredData = sizeItemData.filter(x => (x.maxweight <= 20 ? x.maxweight * 1000 : x.maxweight) === comparisonWeight);

    if (this.weightvalue !== 0) {
      let clubbedProducts = sizeItemData
        .filter(x => x.maxweight.toString().includes('upto'))
        .filter(x => Number(x.maxweight.toString().toLocaleLowerCase().replace('upto', '')) - comparisonWeight >= 0);
      weightFilteredData = weightFilteredData.concat(clubbedProducts);
    }

    if (weightFilteredData.length === 0 && this.weightvalue !== 0) {
      weightFilteredData = sizeItemData.filter(x => (x.maxweight <= 20 ? x.maxweight * 1000 : x.maxweight) >= comparisonWeight);

      if (weightFilteredData.length > 0) {
        //sort based on the maxvalue
        weightFilteredData.sort((a, b) => {
          return a.maxweight - b.maxweight;
        });

        // map to get the next unique item
        const uniqueWeightOptions = [...new Map(weightFilteredData.reverse().map(item => [item['id'], item])).values()];
        if (uniqueWeightOptions.length > 0) {
          weightFilteredData = uniqueWeightOptions;
        }
      }
    }

    //10000 to handle the null values in the rank column, to keep such products at the end of the list
    weightFilteredData.sort((a, b) => {
      var ra = a.rank ? a.rank : 10000;
      var rb = b.rank ? b.rank : 10000;

      var ua = a.highestrankusp ? a.highestrankusp : 'z';
      var ub = b.highestrankusp ? b.highestrankusp : 'z';

      const compareUsp = ua.localeCompare(ub);
      const compareRank = ra - rb;

      return compareUsp || compareRank;
    });

    this.filteredItems = weightFilteredData;
  }

  render() {
    return (
      <div class="findprice-form">
        <fieldset>
          <legend class="heading">{state.i18n.sizeheading}</legend>
          <div class="sizecategory">
            <table class="sizeTable">
              <img id="clouds" src={clouds}></img>
              <tr>
                <td>
                  <img class="firstCell" src={person}></img>
                </td>
                <td>
                  <img src={small}></img>
                </td>
                <td>
                  <img src={medium}></img>
                </td>
                <td>
                  <img src={large}></img>
                </td>
              </tr>
              <tr>
                <td></td>
                {sizeCategories.map(sizeCategory => (
                  <td>
                    <pn-radio-button
                      id={'findpricecategory-' + sizeCategory.value}
                      radioid={sizeCategory.value}
                      value={sizeCategory.value}
                      name="sizecategory"
                      checked={sizeCategory.value === this.sizecategory.value}
                      onChange={() => {
                        this.sizecategory = (sizeCategories as FindPriceSizeCategory[]).filter(x => x.value === sizeCategory.value)[0];

                        this.weight = { value: 0, name: '' } as FindPriceWeight;
                        this.weightvalue = this.weight.value;
                      }}
                    ></pn-radio-button>
                  </td>
                ))}
              </tr>
            </table>
          </div>
        </fieldset>

        <fieldset>
          <legend class="heading">{state.i18n.weightheading}</legend>
          {this.sizecategory.value && weights[this.sizecategory.unit] ? (
            <div class="weightoption">
              {
                <div class="weightoption-content">
                  {weights[this.sizecategory.unit].map(wght => (
                    <pn-choice-button
                      id={'findpriceweight-' + wght.value}
                      choiceid={this.sizecategory.value + wght.value}
                      name="weight"
                      type="radio"
                      value={!this.weight.value ? 0 : weights[this.sizecategory.unit].findIndex(x => x.value === this.weightvalue)}
                      checked={wght.value === this.weight.value}
                      onChange={() => {
                        this.weight = (weights[this.sizecategory.unit] as FindPriceWeight[]).filter(x => x.value === wght.value)[0];
                        this.weightvalue = this.weight.value;
                      }}
                    >
                      {wght.name}
                    </pn-choice-button>
                  ))}
                </div>
              }
            </div>
          ) : null}

          {this.filteredItems && this.filteredItems.length > 0 ? (
            <div class="resultlist">
              {state.i18n.resultlistheading ? <div class="resultHeading">{state.i18n.resultlistheading}</div> : null}
              {this.filteredItems.map((item: ProductPriceItem) => (
                <div>
                  <pn-find-price-result item={item}></pn-find-price-result>
                </div>
              ))}
            </div>
          ) : null}
        </fieldset>
      </div>
    );
  }
}
